import classnames from 'classnames';
import ms from 'ms';
import PropTypes from 'prop-types';
import React from 'react';
import { CSSTransition } from 'react-transition-group';

import styles from './styles.module.scss';
import Button from '../Button/index.ts';
import {
  addVariants,
  useSpacing,
  useOverriddenState,
  useTimeout,
  transitionVariants
} from '../utils/index.ts';

export const PLACEMENTS = ['top', 'bottom', 'left', 'right', 'center'];
export const COLORS = ['light', 'dark'];
export const VARIANTS = COLORS.concat(['default', 'dense', 'no-shadow']);
const Snackbar = ({
  placement,
  children,
  className,
  variant,
  isActive,
  isActiveByDefault,
  onDismiss,
  spacing,
  style,
  timeout,
  action,
  actionLabel,
  ...props
}) => {
  const [active, setActive] = useOverriddenState(isActiveByDefault, isActive);
  const spaceStyles = useSpacing(spacing, { style });

  useTimeout(
    () => {
      setActive(false);
      setTimeout(() => {
        if (active) {
          onDismiss();
        }
      }, 250);
    },
    ms(timeout) - 250,
    [active]
  );

  let actionEl;

  if (actionLabel) {
    actionEl = (
      <Button
        className={addVariants('aglmd-snackbar__action', styles, variant)}
        variant="active"
        color="dark"
        onClick={action}
        spacing={{ left: 2 }}
      >
        {actionLabel}
      </Button>
    );
  }

  return (
    <CSSTransition
      classNames={transitionVariants('aglmd-snackbar', styles)}
      in={active}
      timeout={250}
      appear
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={classnames(
          addVariants(
            'aglmd-snackbar',
            styles,
            variant,
            placement,
            active ? 'active' : 'inactive',
            // apply center if right or left is not specified
            !(placement.includes('right') || placement.includes('left')) && 'center'
          ),
          className
        )}
        style={spaceStyles}
        onClick={onDismiss}
        {...props}
      >
        <div
          className={addVariants(
            'aglmd-snackbar__content',
            styles,
            actionLabel ? 'has-action' : 'no-action'
          )}
        >
          {children}
        </div>
        {actionEl}
      </div>
    </CSSTransition>
  );
};

Snackbar.propTypes = {
  /**
   * The direction which the Snackbar should Snackbar toward
   */
  placement: PropTypes.oneOfType([
    PropTypes.oneOf(PLACEMENTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(PLACEMENTS), PropTypes.bool]))
  ]),
  /**
   * The Snackbar content itself
   */
  children: PropTypes.any.isRequired,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Allow customization of the spacing between the Snackbar and activator. See `Spacer` Component
   */
  spacing: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    x: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
    y: PropTypes.oneOfType([PropTypes.number, PropTypes.bool])
  }),
  /**
   * Custom styles to apply, use sparingly
   */
  style: PropTypes.shape({}),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Whether the Snackbar is open or not, state must be handled by caller
   */
  isActive: PropTypes.bool,
  /**
   * Whether the Snackbar is active by default
   */
  isActiveByDefault: PropTypes.bool,
  /**
   * Fired when the snackbar dismisses itself
   */
  onDismiss: PropTypes.func,
  /**
   * An `ms` expression of how long the snackbar should be shown for
   */
  timeout: PropTypes.string,
  /**
   * Fired when the optional action is clicked
   */
  action: PropTypes.func,
  /**
   * An optional action to provide as part of the snackbar
   */
  actionLabel: PropTypes.string
};

Snackbar.defaultProps = {
  className: '',
  placement: 'bottom',
  variant: 'dark',
  isActive: null,
  isActiveByDefault: false,
  onDismiss: () => 0,
  spacing: {},
  style: {},
  timeout: '7.5s',
  action: () => 0,
  actionLabel: ''
};

export default Snackbar;
